import { Injectable } from '@angular/core';

import { map, Observable } from 'rxjs';

import { EmailMessage, KeyValuePair, PagedResponse } from '@reg/shared/model';
import { HttpServiceHelper } from '@reg/shared/service/http-service-helper';
import { IJsonApiResponse } from '../json-api/json-api.interface';

/**
 * Service to get and save emailMessage data.
 */
@Injectable()
export class EmailMessageService {
    public static INCLUDE_TO_RECIPIENTS: string = 'toRecipients';

    /**
     * Constructor
     */
    constructor(private http: HttpServiceHelper) {
    }

    /**
     * Get all emailMessages for the specified event
     */
    public getIndexListResponse(
        eventId: string,
        includes: string[] = null,
        queryParams: string[] = null,
    ): Observable<PagedResponse> {
        queryParams = this.appendGetFields(true, queryParams);
        const apiRequestUrl: string = this.getApiUrl(eventId, null, includes, queryParams);
        return this.getIndexListResponseByUrl(eventId, apiRequestUrl);
    }

    /**
     * Get emailMessages by a url. This is used for paging through emailMessages since the response
     * provides the proper page urls
     */
    public getIndexListResponseByUrl(eventId: string, url: string): Observable<PagedResponse> {
        return this.http.get(url)
            .pipe(map((response: IJsonApiResponse) => {
                const json: any = response;

                const emailMessages: EmailMessage[] = [];
                for (const emailMessage of json.data) {
                    emailMessages.push((new EmailMessage()).deserialize(emailMessage, json.included));
                }

                return new PagedResponse(
                    json['links']['first'],
                    json['links']['last'],
                    json['links']['prev'],
                    json['links']['next'],
                    json['meta']['total'],
                    emailMessages,
                );
            }));
    }

    /**
     * Get recipient group counts
     */
    public getToRecipientGroupCounts(eventId: string, queryParams: string[]): Observable<KeyValuePair[]> {
        const apiRequestUrl: string = this.getApiUrl(eventId, 'to-recipient-group-counts', null, queryParams);

        return this.http.get(apiRequestUrl)
            .pipe(map((response: IJsonApiResponse) => {
                const emailCount: KeyValuePair[] = [];
                const bodyData: any = response || [];
                for (const item of bodyData) {
                    emailCount.push((new KeyValuePair(item.toRecipientGroupType, item.count)));
                }

                return emailCount;
            }));
    }

    /**
     * Get a single schedule item's data
     */
    public get(eventId: string, emailMessageId: string, includes: string[] = null): Observable<EmailMessage> {
        const queryParams: string[] = this.appendGetFields(false);
        const apiRequestUrl: string = this.getApiUrl(eventId, emailMessageId, includes, queryParams);

        return this.http.get(apiRequestUrl)
            .pipe(map((response: IJsonApiResponse) => (new EmailMessage().deserialize(response, response.included))));
    }

    /**
     * Add or update the emailMessage
     */
    public save(emailMessage: EmailMessage, includes: string[] = null): Observable<EmailMessage> {
        const queryParams: string[] = this.appendGetFields(false);
        let response: Observable<IJsonApiResponse>;
        const apiRequestUrl: string = this.getApiUrl(emailMessage.eventId, emailMessage.id, includes, queryParams);

        if (emailMessage.id) {
            // Update existing EmailMessage
            response = this.http.patch(apiRequestUrl, emailMessage.serialize());
        } else {
            // Add a new EmailMessage
            response = this.http.post(apiRequestUrl, emailMessage.serialize());
        }

        return response
            .pipe(map((response: IJsonApiResponse) => (new EmailMessage()).deserialize(response, response.included)));
    }

    /**
     * Send the emailMessage
     */
    public send(emailMessage: EmailMessage, includes: string[] = null): Observable<EmailMessage> {
        const queryParams: string[] = this.appendGetFields(false);
        const apiRequestUrl: string =
            this.getApiUrl(emailMessage.eventId, `${emailMessage.id}/send`, includes, queryParams);

        return this.http.post(apiRequestUrl, emailMessage.serialize())
            .pipe(map((response: IJsonApiResponse) => (new EmailMessage()).deserialize(response, response.included)));
    }

    /**
     * Delete the specified emailMessage.
     */
    public delete(eventId: string, emailMessageId: string): Observable<boolean> {
        const apiRequestUrl: string = this.getApiUrl(eventId, emailMessageId, null);
        return this.http.delete(apiRequestUrl).pipe(map((response: IJsonApiResponse) => true));
    }

    private appendGetFields(isForList: boolean, queryParams: string[] = []): string[] {
        queryParams = queryParams || [];

        // Only fetch specific fields
        if (queryParams.filter((x: string) => x.indexOf('fields[emailMessage]') !== -1).length === 0) {
            queryParams.push(`fields[emailMessage]=${EmailMessage.getFields(isForList).join(',')}`);
        }

        return queryParams;
    }

    /**
     * Build the api's url based on the given parameters
     */
    private getApiUrl(
        eventId: string,
        emailMessageId?: string,
        includes?: string[],
        queryParams: string[] = null,
    ): string {
        let url: string = `${this.http.configService.getApiBaseUrl()}/events/${eventId}/emails/`;

        if (emailMessageId) {
            url += emailMessageId;
        }

        url += this.http.getQueryParameters(includes, queryParams);

        return url;
    }
}
